import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { FileSpreadsheet, Clock, ChevronLeft, ChevronRight, CalendarDays, RotateCcw } from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import PresenceAvatar from "../../components/presence/PresenceAvatar";
import { exportAttendanceReport } from "../../components/presence/exportAttendance";
import { usePresenceRange, useLivePresence } from "../../components/presence/usePresence";
import {
  DAY_TARGET_MIN,
  LATE_AFTER_KEY,
  STATUS_META,
  TIME_ZONE,
  TINY_RECORD_MIN,
  WEEK_TARGET_MIN,
  avgCheckIn,
  formatClock,
  formatMinutes,
  formatTime,
  lateAfterMinutes,
  lateBy,
  minuteOfDay,
  perPerson,
  readLateAfter,
  statusOf,
  todayIn,
  todaySummary,
  weekOf,
  weekBounds,
  shiftDate,
  fmtDayLabel,
  fmtWeekLabel,
} from "../../components/presence/presence";

const EASE = [0.2, 0.8, 0.2, 1];
const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const Bar = ({ pct, className = "", style, delay = 0 }) => (
  <motion.span
    className={`block h-full ${className}`}
    style={style}
    initial={{ width: reduced() ? `${pct}%` : 0 }}
    animate={{ width: `${pct}%` }}
    transition={{ duration: reduced() ? 0 : 0.9, ease: EASE, delay }}
  />
);

const Card = ({ children, className = "", delay = 0 }) => (
  <motion.section
    initial={reduced() ? false : { opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay }}
    className={`card !p-5 ${className}`}
  >
    {children}
  </motion.section>
);

const CardTitle = ({ title, sub, right }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
    <div className="min-w-0">
      <h3 className="font-display text-[17px] font-semibold text-ink whitespace-nowrap">{title}</h3>
      {sub && <p className="text-[12.5px] text-faint mt-0.5">{sub}</p>}
    </div>
    {right}
  </div>
);

const Kpi = ({ label, value, sub, tone }) => (
  <div className="card !p-4">
    <p className="text-[12.5px] text-faint whitespace-nowrap">{label}</p>
    <p
      className="mt-1.5 font-display text-[28px] leading-none font-semibold tabular-nums whitespace-nowrap"
      style={{ color: tone || "var(--text)" }}
    >
      {value}
    </p>
    <p className="mt-2 text-[12px] text-muted whitespace-nowrap truncate">{sub}</p>
  </div>
);

const Skeleton = ({ h = 120, className = "" }) => (
  <div className={`card !p-0 animate-pulse ${className}`} style={{ height: h }} />
);

// ---------------------------------------------------------------- Who's online
const PersonCard = ({ row, status, lateAfter, live = true }) => {
  const meta = STATUS_META[status];
  const total = row.workedMinutes + row.idleMinutes || 1;
  const late = lateBy(row, lateAfter) > 0;
  return (
    <div
      className={`card !p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-accent-line ${status === "out" ? "opacity-[.62]" : ""}`}
    >
      <div className="flex items-start gap-3">
        <PresenceAvatar userId={row.userId} name={row.displayName} size={42} status={status} />
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-semibold text-ink truncate">{row.displayName}</p>
          <p className="font-mono text-[11.5px] text-faint truncate">@{row.username}</p>
        </div>
        <span className={`pill shrink-0 ${meta.pill || "bg-raise text-muted"}`}>{meta.label}</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          ["Checked in", formatTime(row.firstCheckIn), late],
          ["Worked", formatMinutes(row.workedMinutes)],
          ["Idle", formatMinutes(row.idleMinutes), false, true],
        ].map(([label, value, isLate, amber]) => (
          <div key={label} className="min-w-0">
            <p className="text-[11.5px] text-faint whitespace-nowrap">{label}</p>
            <p className={`font-mono text-[13px] whitespace-nowrap flex items-center gap-1 ${amber ? "text-warn" : "text-ink"}`}>
              {value}
              {isLate && <span className="w-1.5 h-1.5 rounded-full bg-danger" title="Late check-in" />}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-raise overflow-hidden flex">
        <Bar pct={(row.workedMinutes / total) * 100} className="bg-ok" />
        <Bar pct={(row.idleMinutes / total) * 100} className="bg-warn" delay={0.2} />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-[11.5px]">
        <span className="text-faint whitespace-nowrap truncate">
          {row.sessions} session{row.sessions === 1 ? "" : "s"} · {row.idleCount} idle break{row.idleCount === 1 ? "" : "s"}
        </span>
        {live ? (
          status === "out" ? (
            <span className="text-faint whitespace-nowrap">Out {formatTime(row.lastCheckOut)}</span>
          ) : (
            <span className="text-ok whitespace-nowrap">Live · {formatMinutes(row.totalMinutes)}</span>
          )
        ) : row.lastCheckOut ? (
          <span className="text-faint whitespace-nowrap">Out {formatTime(row.lastCheckOut)}</span>
        ) : (
          <span className="text-faint whitespace-nowrap">No check-out</span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Timeline
const AXIS_START = 6 * 60;
const AXIS_END = 24 * 60;
const pos = (min) => `${(Math.min(Math.max(min, AXIS_START), AXIS_END) - AXIS_START) / (AXIS_END - AXIS_START) * 100}%`;

const Timeline = ({ rows, statusBy, lateAfter, now, showNow = true }) => {
  const nowMin = minuteOfDay(now);
  const lateMin = lateAfterMinutes(lateAfter);
  const sorted = [...rows].sort((a, b) => new Date(a.firstCheckIn) - new Date(b.firstCheckIn));
  const ticks = [];
  for (let m = AXIS_START; m <= AXIS_END; m += 120) ticks.push(m);
  const fillOf = (s) => (s === "working" ? "var(--grad)" : s === "idle" ? "var(--amber)" : "var(--line2)");
  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <div className="min-w-[760px]">
        <div className="flex">
          <div className="w-[170px] shrink-0" />
          <div className="relative flex-1 h-5">
            {ticks.map((m) => (
              <span key={m} className="absolute -translate-x-1/2 font-mono text-[10.5px] text-faint" style={{ left: pos(m) }}>
                {String(Math.floor(m / 60) % 24).padStart(2, "0")}:00
              </span>
            ))}
          </div>
          <div className="w-[64px] shrink-0" />
        </div>
        <div className="relative">
          {/* grid, late line, now line */}
          <div className="absolute inset-0 flex pointer-events-none">
            <div className="w-[170px] shrink-0" />
            <div className="relative flex-1">
              {ticks.map((m) => (
                <span key={m} className="absolute top-0 bottom-0 w-px bg-line" style={{ left: pos(m) }} />
              ))}
              <span
                className="absolute top-0 bottom-0 border-l border-dashed"
                style={{ left: pos(lateMin), borderColor: "var(--red)" }}
                title={`Late after ${formatClock(lateMin)}`}
              />
              {showNow && nowMin >= AXIS_START && (
                <>
                  <span className="absolute top-0 bottom-0 w-0.5 bg-accent" style={{ left: pos(nowMin), boxShadow: "0 0 10px var(--accent)" }} />
                  <span className="absolute -bottom-5 -translate-x-1/2 font-mono text-[10px] font-semibold text-accent" style={{ left: pos(nowMin) }}>
                    NOW
                  </span>
                </>
              )}
            </div>
            <div className="w-[64px] shrink-0" />
          </div>
          {sorted.map((r) => {
            const s = statusBy.get(r.userId);
            const late = lateBy(r, lateAfter) > 0;
            const spans = (r.spans?.length ? r.spans : [{ checkIn: r.firstCheckIn, checkOut: r.lastCheckOut }]).filter(
              (sp) => sp.checkIn,
            );
            return (
              <div key={`${r.userId}:${r.date}`} className="relative flex items-center h-9">
                <div className="w-[170px] shrink-0 flex items-center gap-2 min-w-0 pr-2">
                  <PresenceAvatar userId={r.userId} name={r.displayName} size={22} />
                  <span className="text-[13px] text-ink truncate">{r.displayName}</span>
                  {late && <span className="w-1.5 h-1.5 rounded-full bg-danger shrink-0" title="Late check-in" />}
                </div>
                <div className="relative flex-1 h-4">
                  {spans.map((sp, i) => {
                    // A session started the previous evening is drawn from the axis start.
                    const startMin = new Date(sp.checkIn) < new Date(`${todayIn(now)}T00:00:00+03:00`) ? AXIS_START : minuteOfDay(sp.checkIn);
                    const open = !sp.checkOut;
                    // Open session: to now while live, otherwise check-in + the day's total.
                    const endMin = open ? (showNow ? nowMin : startMin + (r.totalMinutes || 0)) : minuteOfDay(sp.checkOut);
                    if (endMin < AXIS_START) return null;
                    const fill = open ? fillOf(s) : "var(--line2)";
                    return (
                      <span
                        key={i}
                        className="absolute top-0 h-4 rounded-md"
                        style={{
                          left: pos(startMin),
                          width: `calc(${pos(Math.max(endMin, startMin + 4))} - ${pos(startMin)})`,
                          background: fill,
                        }}
                        title={`${formatTime(sp.checkIn)} – ${open ? "now" : formatTime(sp.checkOut)}`}
                      />
                    );
                  })}
                </div>
                <div className="w-[64px] shrink-0 text-right font-mono text-[12px] text-muted">{formatMinutes(r.workedMinutes)}</div>
              </div>
            );
          })}
        </div>
        <div className="h-6" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Heatmap
const HOURS = Array.from({ length: 16 }, (_, i) => 8 + i);
const Heatmap = ({ rows, now }) => {
  const days = [...new Set(rows.map((r) => r.date))].sort();
  const grid = days.map((date) =>
    HOURS.map((h) => {
      const startH = new Date(`${date}T${String(h).padStart(2, "0")}:00:00+03:00`).getTime();
      const endH = startH + 3600 * 1000;
      return rows.filter(
        (r) =>
          r.date === date &&
          (r.spans || []).some((sp) => new Date(sp.checkIn).getTime() < endH && (sp.checkOut ? new Date(sp.checkOut).getTime() : now.getTime()) > startH),
      ).length;
    }),
  );
  const max = Math.max(1, ...grid.flat());
  let best = { n: 0, h: null };
  HOURS.forEach((h, i) => {
    const n = grid.reduce((s, row) => s + row[i], 0);
    if (n > best.n) best = { n, h };
  });
  const label = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", day: "numeric", timeZone: "UTC" });
  return (
    <Card delay={0.1}>
      <CardTitle
        title="Activity heatmap"
        sub="People checked in, per hour"
        right={
          best.h !== null && (
            <p className="text-[12.5px] text-faint whitespace-nowrap">
              Busiest hour <span className="text-ink font-medium">{formatClock(best.h * 60)} — {formatClock((best.h + 1) * 60)}</span>
            </p>
          )
        }
      />
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <div className="grid gap-1" style={{ gridTemplateColumns: `64px repeat(${HOURS.length}, minmax(0,1fr))` }}>
            <span />
            {HOURS.map((h) => (
              <span key={h} className="text-center font-mono text-[10.5px] text-faint">
                {String(h).padStart(2, "0")}
              </span>
            ))}
            {days.map((d, di) => (
              <React.Fragment key={d}>
                <span className="text-[12.5px] text-muted whitespace-nowrap self-center">{label(d)}</span>
                {grid[di].map((n, hi) => {
                  const strong = n / max > 0.55;
                  return (
                    <span
                      key={hi}
                      className="h-8 rounded-md flex items-center justify-center font-mono text-[11.5px] font-medium"
                      style={{
                        background: n ? `color-mix(in srgb, var(--accent) ${12 + (n / max) * 78}%, transparent)` : "var(--raise)",
                        color: strong ? "var(--on-accent)" : "var(--text)",
                      }}
                      title={`${n} checked in`}
                    >
                      {n || ""}
                    </span>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------- Bottom grid
const Leaderboard = ({ people, target, title }) => {
  const max = Math.max(target, ...people.map((p) => p.workedMinutes + p.idleMinutes), 1);
  return (
    <Card className="flex flex-col h-full" delay={0.15}>
      <CardTitle title={title} right={<span className="text-[12px] text-faint whitespace-nowrap">┄ {formatMinutes(target).replace(" 0m", "")} target</span>} />
      <ol className="flex-1 flex flex-col justify-between gap-3">
        {people.map((p, i) => {
          const idlePct = p.workedMinutes + p.idleMinutes ? Math.round((p.idleMinutes / (p.workedMinutes + p.idleMinutes)) * 100) : 0;
          return (
            <li key={p.userId} className="flex items-center gap-3">
              <span className={`w-6 font-mono text-[12px] ${i < 3 ? "text-accent-ink font-semibold" : "text-faint"}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <PresenceAvatar userId={p.userId} name={p.displayName} size={28} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13.5px] text-ink truncate">{p.displayName}</span>
                  <span className="font-mono text-[12px] whitespace-nowrap">
                    <span className="text-ink font-semibold">{formatMinutes(p.workedMinutes)}</span>
                    <span className="text-faint"> · {idlePct}% idle</span>
                  </span>
                </div>
                <div className="relative mt-1.5 h-[7px] rounded-full bg-raise overflow-hidden flex">
                  <Bar pct={(p.workedMinutes / max) * 100} className="bg-grad" delay={i * 0.03} />
                  <Bar pct={(p.idleMinutes / max) * 100} className="bg-warn opacity-75" delay={0.2 + i * 0.03} />
                  <span
                    className="absolute top-[-2px] bottom-[-2px] border-l border-dashed border-muted"
                    style={{ left: `${(target / max) * 100}%` }}
                    title="Target"
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

const FocusCard = ({ people }) => {
  const worked = people.reduce((s, p) => s + p.workedMinutes, 0);
  const idle = people.reduce((s, p) => s + p.idleMinutes, 0);
  const pct = worked + idle ? Math.round((worked / (worked + idle)) * 100) : 0;
  const topIdle = [...people]
    .filter((p) => p.workedMinutes + p.idleMinutes > 0)
    .map((p) => ({ ...p, share: Math.round((p.idleMinutes / (p.workedMinutes + p.idleMinutes)) * 100) }))
    .sort((a, b) => b.share - a.share)
    .slice(0, 4);
  return (
    <Card delay={0.2}>
      <CardTitle title="Working vs idle" />
      <div className="flex flex-wrap items-center gap-6">
        <div
          className="relative w-32 h-32 rounded-full shrink-0"
          style={{ background: `conic-gradient(var(--green) 0 ${pct}%, var(--amber) ${pct}% 100%)` }}
        >
          <div className="absolute inset-[14px] rounded-full bg-panel-solid flex flex-col items-center justify-center">
            <span className="font-display text-[24px] font-semibold text-ink leading-none">{pct}%</span>
            <span className="text-[11px] text-faint mt-1">focused</span>
          </div>
        </div>
        <div className="flex-1 min-w-[160px] space-y-2 text-[13px]">
          <p className="flex items-center justify-between gap-3 whitespace-nowrap">
            <span className="flex items-center gap-2 text-muted"><span className="w-2 h-2 rounded-full bg-ok" />Working</span>
            <span className="font-mono font-semibold text-ink">{formatMinutes(worked)}</span>
          </p>
          <p className="flex items-center justify-between gap-3 whitespace-nowrap">
            <span className="flex items-center gap-2 text-muted"><span className="w-2 h-2 rounded-full bg-warn" />Idle</span>
            <span className="font-mono font-semibold text-ink">{formatMinutes(idle)}</span>
          </p>
        </div>
      </div>
      {topIdle.length > 0 && (
        <div className="mt-5 pt-4 border-t border-line">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-faint mb-3">Highest idle share</p>
          <ul className="space-y-2.5">
            {topIdle.map((p) => (
              <li key={p.userId} className="flex items-center gap-2.5">
                <PresenceAvatar userId={p.userId} name={p.displayName} size={22} />
                <span className="w-24 text-[13px] text-ink truncate">{p.displayName}</span>
                <span className="flex-1 h-1 rounded-full bg-raise overflow-hidden">
                  <Bar pct={p.share} className="bg-warn" />
                </span>
                <span className="font-mono text-[11.5px] text-muted whitespace-nowrap">
                  {p.share}% · {formatMinutes(p.idleMinutes)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

const CheckIns = ({ items, title, lateAfter, onLateAfter }) => (
  <Card className="flex-1" delay={0.25}>
    <CardTitle
      title={title}
      right={
        <label
          className="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg border border-line2 bg-panel2 text-[12px] text-faint whitespace-nowrap cursor-pointer hover:text-muted focus-within:border-accent-line"
          title="Check-ins after this time count as late"
        >
          <Clock className="w-3.5 h-3.5" /> Late ·
          <input
            type="time"
            value={lateAfter}
            onChange={(e) => onLateAfter(e.target.value)}
            aria-label="Late after"
            className="w-[76px] bg-transparent font-mono text-ink focus:outline-none cursor-pointer"
          />
        </label>
      }
    />
    {items.length === 0 ? (
      <p className="text-sm text-faint py-4">No check-ins yet.</p>
    ) : (
      <ul className="divide-y divide-[var(--line)]">
        {items.map((it, i) => (
          <li key={it.userId + "-" + i} className="flex items-center gap-3 py-2.5">
            <PresenceAvatar userId={it.userId} name={it.displayName} size={26} />
            <span className="flex-1 min-w-0 text-[13.5px] text-ink truncate">{it.displayName}</span>
            <span className="font-mono text-[13px] text-ink whitespace-nowrap">{formatClock(it.minute)}</span>
            <span className={`pill !h-[22px] !text-[11px] shrink-0 ${it.lateLabel ? "pill-danger" : "pill-ok"}`}>
              {it.lateLabel || "On time"}
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

// ---------------------------------------------------------------- Page
const FILTERS = [
  ["all", "All"],
  ["working", "Working"],
  ["idle", "Idle"],
  ["out", "Checked out"],
];

const TeamPresence = () => {
  // View state lives in the URL (?mode=day&date=YYYY-MM-DD) so a view can be
  // linked and survives a refresh.
  const [params, setParams] = useSearchParams();
  const today = todayIn();
  const mode = params.get("mode") === "week" ? "week" : "day";
  const paramDate = params.get("date");
  const anchor = /^\d{4}-\d{2}-\d{2}$/.test(paramDate || "") && paramDate <= today ? paramDate : today;

  const [filter, setFilter] = useState("all");
  const [lateAfter, setLateAfter] = useState(readLateAfter);
  const { live, data: liveData } = useLivePresence(true, "team");
  const earliest = liveData?.earliest || "2000-01-01";

  const week = weekBounds(anchor);
  const span = mode === "day" ? { from: anchor, to: anchor } : weekOf(anchor, today);
  // Live statuses (working / idle now) only exist for today.
  const isLive = mode === "day" && anchor === today;
  const isCurrent = mode === "day" ? anchor === today : weekBounds(today).from === week.from;

  const { data, error, loading } = usePresenceRange(span, { live: isLive, scope: "team" });
  const rows = useMemo(() => data?.rows || [], [data]);
  const now = new Date();

  // Put the current view in the URL on first load, so it can be copied / linked.
  useEffect(() => {
    if (params.get("mode") && params.get("date") === anchor) return;
    const p = new URLSearchParams(params);
    p.set("mode", mode);
    p.set("date", anchor);
    setParams(p, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setView = (next) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("mode", next.mode ?? mode);
        p.set("date", next.date ?? anchor);
        return p;
      },
      { replace: true },
    );
    if (next.date && next.date !== anchor) setFilter("all");
  };

  // Arrows move 1 day / 7 days and stop at the bot's first record and today.
  const step = mode === "day" ? 1 : 7;
  const prevDate = shiftDate(anchor, -step);
  const nextDate = shiftDate(anchor, step);
  const canPrev = mode === "day" ? prevDate >= earliest : weekBounds(prevDate).to >= earliest;
  const canNext = mode === "day" ? nextDate <= today : weekBounds(nextDate).from <= today;
  const go = (dir) => {
    const next = dir < 0 ? prevDate : nextDate;
    setView({ date: next > today ? today : next });
  };

  /** Day ⇄ Week: keep the same week; coming back, land on a day that has data. */
  const switchMode = (next) => {
    if (next === mode) return;
    if (next === "week") return setView({ mode: "week" });
    if (weekBounds(today).from === week.from) return setView({ mode: "day", date: today });
    const withData = [...new Set(rows.map((r) => r.date))].sort();
    setView({ mode: "day", date: withData[withData.length - 1] || week.to });
  };

  const saveLateAfter = (v) => {
    if (!/^\d{2}:\d{2}$/.test(v)) return;
    setLateAfter(v);
    try {
      localStorage.setItem(LATE_AFTER_KEY, v);
    } catch {
      /* not remembered */
    }
  };

  // On a past day nobody is live: everyone reads as checked out.
  const statusBy = useMemo(
    () => new Map(rows.map((r) => [r.userId, isLive ? statusOf(r, live) : "out"])),
    [rows, live, isLive],
  );
  const people = useMemo(() => perPerson(rows).sort((a, b) => b.workedMinutes - a.workedMinutes), [rows]);
  const sumToday = todaySummary(rows, isLive ? live : null, lateAfter);

  const navLabel = mode === "day" ? fmtDayLabel(anchor, today) : fmtWeekLabel(week, today);

  const counts = { all: rows.length, working: sumToday.working, idle: sumToday.idle, out: sumToday.out };
  const order = { working: 0, idle: 1, out: 2 };
  const cards = rows
    .filter((r) => filter === "all" || statusBy.get(r.userId) === filter)
    .sort((a, b) => order[statusBy.get(a.userId)] - order[statusBy.get(b.userId)] || b.workedMinutes - a.workedMinutes);

  // Check-ins (today: each first check-in; week: each person's average).
  const checkIns =
    mode === "day"
      ? rows
          .filter((r) => r.workedMinutes >= TINY_RECORD_MIN)
          .map((r) => {
            const lb = lateBy(r, lateAfter);
            return { userId: r.userId, displayName: r.displayName, minute: minuteOfDay(r.firstCheckIn), lateLabel: lb ? `+${formatMinutes(lb).replace(" 0m", "")} late` : "" };
          })
          .sort((a, b) => a.minute - b.minute)
      : people
          .map((p) => {
            const avg = avgCheckIn(p.days);
            const lateDays = p.days.filter((d) => lateBy(d, lateAfter) > 0).length;
            return avg === null ? null : { userId: p.userId, displayName: p.displayName, minute: avg, lateLabel: lateDays ? `${lateDays}× late` : "" };
          })
          .filter(Boolean)
          .sort((a, b) => a.minute - b.minute);

  const days = new Set(rows.map((r) => r.date)).size;
  const totalWorked = rows.reduce((t, r) => t + r.workedMinutes, 0);
  const totalIdle = rows.reduce((t, r) => t + r.idleMinutes, 0);
  const idleShare = totalWorked + totalIdle ? Math.round((totalIdle / (totalWorked + totalIdle)) * 100) : 0;
  const rangeAvg = avgCheckIn(rows);
  const rangeLate = rows.filter((r) => lateBy(r, lateAfter) > 0).length;

  return (
    <PageWrapper
      title={
        "Team Presence"
      }
      subtitle={
        isLive ? (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className="w-[7px] h-[7px] rounded-full bg-ok animate-pulse" style={{ boxShadow: "0 0 8px var(--green)" }} />
            Live from the ABS Discord bot · {TIME_ZONE} · updated {data ? formatTime(data.generatedAt) : "…"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className="w-[7px] h-[7px] rounded-full bg-faint" />
            From the ABS Discord bot · {TIME_ZONE} · {mode === "day" ? fmtDayLabel(anchor, today).replace(" · ", ", ") : fmtWeekLabel(week, today)}
          </span>
        )
      }
      actions={
        <>
          <div className="flex items-center h-10 p-1 rounded-xl border border-line2 bg-panel" role="tablist" aria-label="Range">
            {[
              ["day", "Day"],
              ["week", "Week"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={mode === key}
                onClick={() => switchMode(key)}
                className={`h-8 px-4 rounded-lg text-[13px] whitespace-nowrap transition-colors ${
                  mode === key ? "bg-panel-solid shadow-card text-ink font-medium" : "text-muted hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center h-[38px] rounded-xl border border-line2 bg-panel">
            <button
              type="button"
              onClick={() => go(-1)}
              disabled={!canPrev}
              aria-label={mode === "day" ? "Previous day" : "Previous week"}
              className="h-full w-9 rounded-l-xl flex items-center justify-center text-muted hover:text-ink hover:bg-hover disabled:opacity-[.35] disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-muted"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="inline-flex items-center gap-2 px-2.5 text-[13.5px] font-medium text-ink whitespace-nowrap">
              <CalendarDays className="w-4 h-4 text-accent" />
              {navLabel}
            </span>
            <button
              type="button"
              onClick={() => go(1)}
              disabled={!canNext}
              aria-label={mode === "day" ? "Next day" : "Next week"}
              className="h-full w-9 rounded-r-xl flex items-center justify-center text-muted hover:text-ink hover:bg-hover disabled:opacity-[.35] disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-muted"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {!isCurrent && (
            <button
              type="button"
              onClick={() => setView({ date: today })}
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl border border-accent-line bg-accent-soft text-accent-ink text-[13px] font-medium whitespace-nowrap hover:brightness-110"
            >
              <RotateCcw className="w-4 h-4" />
              {mode === "day" ? "Today" : "This week"}
            </button>
          )}

          <button type="button" className="btn-ghost" disabled={!rows.length} onClick={() => exportAttendanceReport(rows, span.from, span.to)}>
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-5 p-4 rounded-2xl border border-danger/30 bg-danger-soft text-danger text-sm">{error}</div>
      )}

      {/* Empty day / week: just one card, no KPIs and no analytics. */}
      {!loading && !error && rows.length === 0 ? (
        <div className="card !border-dashed text-center py-14">
          <span className="mx-auto mb-4 w-11 h-11 rounded-xl bg-accent-soft text-accent flex items-center justify-center">
            <CalendarDays className="w-5 h-5" />
          </span>
          <p className="font-display text-[17px] font-semibold text-ink">
            {mode === "day" ? "No check-ins on this day" : "No check-ins this week"}
          </p>
          <p className="text-sm text-faint mt-1.5">
            Weekend, holiday, or the bot has no records for {navLabel.replace(/^[^·]*· /, "")}.
          </p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
            {loading && !data ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={104} />)
            ) : isLive ? (
              <>
                <Kpi label="Working now" value={sumToday.working} sub={`of ${sumToday.total} checked in today`} tone="var(--green)" />
                <Kpi label="Idle now" value={sumToday.idle} sub="idle in Discord" tone="var(--amber)" />
                <Kpi label="Checked out" value={sumToday.out} sub="done for today" />
                <Kpi label="Avg check-in" value={sumToday.avgCheckIn === null ? "—" : formatClock(sumToday.avgCheckIn)} sub={`late after ${formatClock(lateAfterMinutes(lateAfter))}`} />
                <Kpi label="Late today" value={sumToday.late} sub={`checked in after ${formatClock(lateAfterMinutes(lateAfter))}`} tone={sumToday.late ? "var(--red)" : undefined} />
              </>
            ) : (
              <>
                <Kpi
                  label="Team hours"
                  value={formatMinutes(totalWorked)}
                  sub={mode === "day" ? `${people.length} ${people.length === 1 ? "person" : "people"} checked in` : `${days} working day${days === 1 ? "" : "s"}`}
                />
                <Kpi
                  label="Avg per person"
                  value={people.length ? formatMinutes(totalWorked / people.length) : "—"}
                  sub={mode === "day" ? "target 8h / day" : "target 32h / week"}
                />
                <Kpi label="Idle share" value={`${idleShare}%`} sub={`${formatMinutes(totalIdle)} idle in total`} tone="var(--amber)" />
                <Kpi label="Avg check-in" value={rangeAvg === null ? "—" : formatClock(rangeAvg)} sub={`late after ${formatClock(lateAfterMinutes(lateAfter))}`} />
                <Kpi
                  label="Late check-ins"
                  value={rangeLate}
                  sub={mode === "day" ? "people came in late" : `across ${days} day${days === 1 ? "" : "s"}`}
                  tone={rangeLate ? "var(--red)" : undefined}
                />
              </>
            )}
          </div>

          {mode === "day" ? (
            <>
              {/* Who's online / Attendance */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-display text-[19px] font-semibold text-ink whitespace-nowrap">{isLive ? "Who\u2019s online" : "Attendance"}</h2>
                <div className="flex flex-wrap gap-2">
                  {FILTERS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilter(key)}
                      className={`h-8 px-3 rounded-full border text-[13px] whitespace-nowrap transition-colors ${
                        filter === key ? "bg-accent-soft border-accent-line text-accent-ink" : "bg-panel border-line2 text-muted hover:text-ink"
                      }`}
                    >
                      {label} <span className="font-mono text-[11.5px] opacity-80">{counts[key]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))" }}>
                {loading && !data
                  ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={170} />)
                  : cards.map((r) => <PersonCard key={`${r.userId}:${r.date}`} row={r} status={statusBy.get(r.userId)} lateAfter={lateAfter} live={isLive} />)}
              </div>

              <Card className="mb-6" delay={0.1}>
                <CardTitle
                  title={isLive ? "Today's timeline" : "Timeline"}
                  sub="First check-in to last check-out, per person"
                  right={
                    <div className="flex flex-wrap gap-3 text-[12px] text-muted">
                      {[
                        ["var(--grad)", "Working"],
                        ["var(--amber)", "Idle now"],
                        ["var(--line2)", "Checked out"],
                      ].map(([bg, l]) => (
                        <span key={l} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <span className="w-3 h-2 rounded-sm" style={{ background: bg }} />
                          {l}
                        </span>
                      ))}
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-danger" />
                        Late
                      </span>
                    </div>
                  }
                />
                <Timeline
                  rows={rows}
                  statusBy={statusBy}
                  lateAfter={lateAfter}
                  now={isLive ? now : new Date(`${anchor}T23:59:00+03:00`)}
                  showNow={isLive}
                />
              </Card>
            </>
          ) : (
            <div className="mb-6">
              <Heatmap rows={rows} now={now} />
            </div>
          )}
        </>
      )}

      {rows.length > 0 && (
        <div className="grid gap-4 items-stretch" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
          <Leaderboard
            people={people}
            target={mode === "day" ? DAY_TARGET_MIN : WEEK_TARGET_MIN}
            title={mode === "day" ? (isLive ? "Hours today" : "Hours") : isCurrent ? "Hours this week" : "Hours"}
          />
          <div className="flex flex-col gap-[14px]">
            <FocusCard people={people} />
            <CheckIns items={checkIns} title={mode === "day" ? (isLive ? "Check-ins today" : "Check-ins") : "Average check-in"} lateAfter={lateAfter} onLateAfter={saveLateAfter} />
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default TeamPresence;
